import firebase from 'firebase/compat/app';
import Economy from 'dt-common/constants/Economy';
import { PaymentStatus, StripePaymentResult } from 'dt-common/generated/graphql-types';
import Config from '~/constants/Config';
import { CurrencyDispatcher } from '~/flux/dispatchers';
import mutationCreateStripePaymentIntent from '~/graphql/payments/mutationCreateStripePaymentIntent';
import { logError } from '~/logger';
import { PaymentsStoreMessage } from '../messages';

export default async function requestNewPaymentIntent(
  {
    playerId,
    setPaymentSucceededData,
    setCardDeclinedData,
    setPaymentRequiresActionData,
    emit,
  }: {
    playerId: string | null;
    setPaymentSucceededData: (val: StripePaymentResult) => void;
    setCardDeclinedData: (val: StripePaymentResult) => void;
    setPaymentRequiresActionData: (val: StripePaymentResult) => void;
    emit: (message: PaymentsStoreMessage, data?: unknown) => void;
  },
  action: { purchase_selection: { id: string }; payment_method_id: string }
) {
  if (!playerId) throw new Error('playerId required');

  try {
    const result = await mutationCreateStripePaymentIntent({
      playerId,
      purchaseSelectionId: action.purchase_selection.id,
      paymentMethodId: action.payment_method_id,
      platform: Config.PLATFORM,
    });

    switch (result.status) {
      case PaymentStatus.Succeeded: {
        setPaymentSucceededData(result);
        emit('PAYMENT_SUCCEEDED', result);

        if (result.currency1 != null) {
          CurrencyDispatcher.handleCurrencyAction({
            actionType: CurrencyDispatcher.NEW_CURRENCY_BALANCE,
            currency1: result.currency1,
          });
        }

        if (process.env.DT_TIER === 'production' && result.purchaseSelectionId) {
          const productID = result.purchaseSelectionId;
          const item = (Economy.PREMIUM_ITEM_DICT as Record<string, { usd_cents: number }>)[productID];
          firebase.analytics().logEvent('purchase', {
            value: item.usd_cents / 100,
            currency: 'USD',
            transaction_id: `stripe-${playerId}-${productID}-${result.chargeAmount ?? 0}`,
            items: [{ item_id: productID }],
          });
        }
        return;
      }

      case PaymentStatus.CardDeclined: {
        setCardDeclinedData(result);
        emit('CARD_DECLINED', result);
        return;
      }

      case PaymentStatus.RequiresAction: {
        setPaymentRequiresActionData(result);
        emit('PAYMENT_REQUIRES_ACTION', result);
        return;
      }

      default:
        emit('PAYMENT_ERROR');
    }
  } catch (err) {
    logError(err, {
      module: 'PaymentsStore',
      func: 'requestNewPaymentIntent',
    });
    emit('PAYMENT_ERROR');
  }
}
